import * as SchemaTypes from 'lib/home/generated/types/api';

import { useQuery, UseQueryOptions, useMutation, UseMutationOptions } from 'react-query';
import { graphqlFetcher } from 'lib/util';
export const DietaryInformationFieldsFragmentDoc = `
    fragment DietaryInformationFields on VariantDietaryInformation {
  allergens
  diets
  ingredients
  nutrients {
    calories
    carbohydrate
    cholesterol
    fat
    fat_saturated
    fat_trans
    fiber
    uncounted_fiber
    sugar
    added_sugar
    sugar_alcohol
    sodium
    calcium
    iron
    vitamin_c
    vitamin_a_IU
    potassium
    fat_monounsaturated
    fat_polyunsaturated
    magnesium
    phosphorus
    zinc
    copper
    manganese
    selenium
    thiamin
    riboflavin
    niacin
    vitamin_b6
    folate
    folic_acid
    chlorine
    vitamin_b12
    vitamin_e
    vitamin_d_IU
    vitamin_k
    protein
  }
}
    `;
export const ProductVariantFieldsFragmentDoc = `
    fragment ProductVariantFields on MedusaProductVariant {
  id
  title
  sku
  options {
    id
    value
    option_id
    variant_id
  }
  variant_preference {
    id
    user_id
    product_variant_id
    favorite
    never
  }
  dietary_information {
    ...DietaryInformationFields
  }
  prices {
    id
    currency_code
    amount
    sale_amount
  }
  product_id
  product {
    id
    title
    description
    discountable
    is_giftcard
    tags {
      id
    }
    handle
    thumbnail
    variants {
      id
      product_id
      sku
      title
    }
    collection {
      id
      title
      handle
    }
  }
  metadata
}
    ${DietaryInformationFieldsFragmentDoc}`;
export const UserFieldsFragmentDoc = `
    fragment UserFields on User {
  id
  first_name
  last_name
  is_active
  email
  email_verified
  phone
  created_at
  updated_at
  postal_code
  stripe_customer_id
  impersonating
  medusa_shipping_address_id
  shipping_addresses {
    id
    first_name
    last_name
    line1
    line2
    city
    state
    country
    postal_code
    is_default
  }
  google_oauth_id
  facebook_oauth_id
  meal_preferences {
    allergens
    diets
  }
  subscription {
    id
    user_id
    start_date
    end_date
    frequency
    product_variant_id
    product_variant {
      ...ProductVariantFields
    }
    status
    status_reason
  }
}
    ${ProductVariantFieldsFragmentDoc}`;
export const ShippingAddressFieldsFragmentDoc = `
    fragment ShippingAddressFields on MedusaAddress {
  first_name
  last_name
  address_1
  address_2
  city
  country_code
  province
  postal_code
}
    `;
export const StripePaymentMethodFieldsFragmentDoc = `
    fragment StripePaymentMethodFields on StripePaymentMethod {
  id
  is_default
  billing_details {
    name
    email
    phone
    address {
      line1
      line2
      city
      state
      postal_code
      country
    }
  }
  card {
    brand
    last4
    exp_month
    exp_year
    country
  }
  customer_id
}
    `;
export const UserAddressFieldsFragmentDoc = `
    fragment UserAddressFields on StandardAddress {
  id
  customer_id
  company
  first_name
  last_name
  line1
  line2
  city
  state
  postal_code
  country
  phone
  is_default
}
    `;
export const OrderFieldsFragmentDoc = `
    fragment OrderFields on SubscriptionOrder {
  id
  complete_order_at
  created_at
  medusa_cart_id
  processed_at
  skip
  status
  subscription_id
  updated_at
  week
  year
}
    `;
export const MedusaCartFieldsFragmentDoc = `
    fragment MedusaCartFields on MedusaCart {
  id
  email
  billing_address_id
  shipping_address_id
  region_id
  customer_id
  type
  completed_at
  payment_authorized_at
  created_at
  updated_at
  deleted_at
  metadata
  shipping_total
  discount_total
  tax_total
  total
  subtotal
  gift_card_total
}
    `;
export const MedusaCartItemFieldsFragmentDoc = `
    fragment MedusaCartItemFields on MedusaLineItem {
  id
  description
  title
  quantity
  allow_discounts
  has_shipping
  unit_price
  variant_id
  is_giftcard
  thumbnail
  metadata
  created_at
}
    `;
export const MedusaDiscountFieldsFragmentDoc = `
    fragment MedusaDiscountFields on MedusaDiscount {
  id
  code
  is_dynamic
  rule_id
  rule {
    id
    description
    type
    value
    allocation
  }
  is_disabled
  usage_limit
  usage_count
  is_recurring
}
    `;
export const GiftCardFieldsFragmentDoc = `
    fragment GiftCardFields on MedusaGiftCard {
  id
  code
  balance
  value
  is_disabled
}
    `;
export const SubscriptionFieldsFragmentDoc = `
    fragment SubscriptionFields on SubscriptionModel {
  id
  user_id
  start_date
  end_date
  frequency
  product_variant_id
  product_variant {
    ...ProductVariantFields
  }
  status
  status_reason
  current_order {
    ...OrderFields
    medusa_cart {
      ...MedusaCartFields
      items {
        ...MedusaCartItemFields
        variant {
          ...ProductVariantFields
        }
      }
      discounts {
        ...MedusaDiscountFields
      }
      gift_cards {
        ...GiftCardFields
      }
    }
  }
  upcoming_orders {
    ...OrderFields
  }
  created_at
  updated_at
}
    ${ProductVariantFieldsFragmentDoc}
${OrderFieldsFragmentDoc}
${MedusaCartFieldsFragmentDoc}
${MedusaCartItemFieldsFragmentDoc}
${MedusaDiscountFieldsFragmentDoc}
${GiftCardFieldsFragmentDoc}`;
export const CurrentCartDocument = `
    query CurrentCart {
  me {
    subscription {
      ...SubscriptionFields
    }
  }
}
    ${SubscriptionFieldsFragmentDoc}`;
export const useCurrentCartQuery = <TData = SchemaTypes.CurrentCartQuery, TError = unknown>(
  variables?: SchemaTypes.CurrentCartQueryVariables,
  options?: UseQueryOptions<SchemaTypes.CurrentCartQuery, TError, TData>
) =>
  useQuery<SchemaTypes.CurrentCartQuery, TError, TData>(
    variables === undefined ? ['CurrentCart'] : ['CurrentCart', variables],
    graphqlFetcher<SchemaTypes.CurrentCartQuery, SchemaTypes.CurrentCartQueryVariables>(CurrentCartDocument, variables),
    options
  );
export const AddDiscountCodeToCartDocument = `
    mutation AddDiscountCodeToCart($addDiscountCodeInput: AddDiscountCodeInput!) {
  addDiscountCodeToCart(input: $addDiscountCodeInput) {
    ...MedusaDiscountFields
  }
}
    ${MedusaDiscountFieldsFragmentDoc}`;
export const useAddDiscountCodeToCartMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.AddDiscountCodeToCartMutation,
    TError,
    SchemaTypes.AddDiscountCodeToCartMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.AddDiscountCodeToCartMutation,
    TError,
    SchemaTypes.AddDiscountCodeToCartMutationVariables,
    TContext
  >(
    ['AddDiscountCodeToCart'],
    (variables?: SchemaTypes.AddDiscountCodeToCartMutationVariables) =>
      graphqlFetcher<SchemaTypes.AddDiscountCodeToCartMutation, SchemaTypes.AddDiscountCodeToCartMutationVariables>(
        AddDiscountCodeToCartDocument,
        variables
      )(),
    options
  );
export const PreviewDraftOrderDocument = `
    query PreviewDraftOrder($input: CreateUserSubscriptionInput!) {
  previewDraftOrder(input: $input) {
    id
    cart {
      ...MedusaCartFields
      items {
        ...MedusaCartItemFields
        variant {
          ...ProductVariantFields
        }
      }
      discounts {
        ...MedusaDiscountFields
      }
    }
  }
}
    ${MedusaCartFieldsFragmentDoc}
${MedusaCartItemFieldsFragmentDoc}
${ProductVariantFieldsFragmentDoc}
${MedusaDiscountFieldsFragmentDoc}`;
export const usePreviewDraftOrderQuery = <TData = SchemaTypes.PreviewDraftOrderQuery, TError = unknown>(
  variables: SchemaTypes.PreviewDraftOrderQueryVariables,
  options?: UseQueryOptions<SchemaTypes.PreviewDraftOrderQuery, TError, TData>
) =>
  useQuery<SchemaTypes.PreviewDraftOrderQuery, TError, TData>(
    ['PreviewDraftOrder', variables],
    graphqlFetcher<SchemaTypes.PreviewDraftOrderQuery, SchemaTypes.PreviewDraftOrderQueryVariables>(
      PreviewDraftOrderDocument,
      variables
    ),
    options
  );
export const UpdateDraftOrderDocument = `
    mutation UpdateDraftOrder($input: UpdateDraftOrderInput!) {
  updateDraftOrder(input: $input) {
    cart {
      ...MedusaCartFields
    }
  }
}
    ${MedusaCartFieldsFragmentDoc}`;
export const useUpdateDraftOrderMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdateDraftOrderMutation,
    TError,
    SchemaTypes.UpdateDraftOrderMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.UpdateDraftOrderMutation, TError, SchemaTypes.UpdateDraftOrderMutationVariables, TContext>(
    ['UpdateDraftOrder'],
    (variables?: SchemaTypes.UpdateDraftOrderMutationVariables) =>
      graphqlFetcher<SchemaTypes.UpdateDraftOrderMutation, SchemaTypes.UpdateDraftOrderMutationVariables>(
        UpdateDraftOrderDocument,
        variables
      )(),
    options
  );
export const EmailPreviewDocument = `
    query EmailPreview($templateName: String!) {
  email_preview(templateName: $templateName) {
    html
  }
}
    `;
export const useEmailPreviewQuery = <TData = SchemaTypes.EmailPreviewQuery, TError = unknown>(
  variables: SchemaTypes.EmailPreviewQueryVariables,
  options?: UseQueryOptions<SchemaTypes.EmailPreviewQuery, TError, TData>
) =>
  useQuery<SchemaTypes.EmailPreviewQuery, TError, TData>(
    ['EmailPreview', variables],
    graphqlFetcher<SchemaTypes.EmailPreviewQuery, SchemaTypes.EmailPreviewQueryVariables>(
      EmailPreviewDocument,
      variables
    ),
    options
  );
export const SendEmailPreviewDocument = `
    mutation SendEmailPreview($templateName: String!, $to: String!) {
  send_email_preview(templateName: $templateName, to: $to)
}
    `;
export const useSendEmailPreviewMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.SendEmailPreviewMutation,
    TError,
    SchemaTypes.SendEmailPreviewMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.SendEmailPreviewMutation, TError, SchemaTypes.SendEmailPreviewMutationVariables, TContext>(
    ['SendEmailPreview'],
    (variables?: SchemaTypes.SendEmailPreviewMutationVariables) =>
      graphqlFetcher<SchemaTypes.SendEmailPreviewMutation, SchemaTypes.SendEmailPreviewMutationVariables>(
        SendEmailPreviewDocument,
        variables
      )(),
    options
  );
export const RegisterDocument = `
    mutation Register($createUserInput: CreateUserInput!) {
  register(createUserInput: $createUserInput) {
    hashed_password
    JWT
  }
}
    `;
export const useRegisterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<SchemaTypes.RegisterMutation, TError, SchemaTypes.RegisterMutationVariables, TContext>
) =>
  useMutation<SchemaTypes.RegisterMutation, TError, SchemaTypes.RegisterMutationVariables, TContext>(
    ['Register'],
    (variables?: SchemaTypes.RegisterMutationVariables) =>
      graphqlFetcher<SchemaTypes.RegisterMutation, SchemaTypes.RegisterMutationVariables>(
        RegisterDocument,
        variables
      )(),
    options
  );
export const FinishRegistrationDocument = `
    mutation FinishRegistration($validateAccountInput: ValidateAccountInput!) {
  finishRegistration(validateAccountInput: $validateAccountInput) {
    user {
      id
      email
    }
    token
  }
}
    `;
export const useFinishRegistrationMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.FinishRegistrationMutation,
    TError,
    SchemaTypes.FinishRegistrationMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.FinishRegistrationMutation,
    TError,
    SchemaTypes.FinishRegistrationMutationVariables,
    TContext
  >(
    ['FinishRegistration'],
    (variables?: SchemaTypes.FinishRegistrationMutationVariables) =>
      graphqlFetcher<SchemaTypes.FinishRegistrationMutation, SchemaTypes.FinishRegistrationMutationVariables>(
        FinishRegistrationDocument,
        variables
      )(),
    options
  );
export const FinishOnboardingDocument = `
    mutation FinishOnboarding($finishOnboardingInput: FinishOnboardingInput!) {
  finishOnboarding(finishOnboardingInput: $finishOnboardingInput) {
    id
    status
    user_id
  }
}
    `;
export const useFinishOnboardingMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.FinishOnboardingMutation,
    TError,
    SchemaTypes.FinishOnboardingMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.FinishOnboardingMutation, TError, SchemaTypes.FinishOnboardingMutationVariables, TContext>(
    ['FinishOnboarding'],
    (variables?: SchemaTypes.FinishOnboardingMutationVariables) =>
      graphqlFetcher<SchemaTypes.FinishOnboardingMutation, SchemaTypes.FinishOnboardingMutationVariables>(
        FinishOnboardingDocument,
        variables
      )(),
    options
  );
export const SetPasswordDocument = `
    mutation SetPassword($setPasswordInput: SetPasswordInput!) {
  setPassword(setPasswordInput: $setPasswordInput) {
    email
  }
}
    `;
export const useSetPasswordMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.SetPasswordMutation,
    TError,
    SchemaTypes.SetPasswordMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.SetPasswordMutation, TError, SchemaTypes.SetPasswordMutationVariables, TContext>(
    ['SetPassword'],
    (variables?: SchemaTypes.SetPasswordMutationVariables) =>
      graphqlFetcher<SchemaTypes.SetPasswordMutation, SchemaTypes.SetPasswordMutationVariables>(
        SetPasswordDocument,
        variables
      )(),
    options
  );
export const UpdatePreferencesDocument = `
    mutation UpdatePreferences($updatePreferencesInput: MealPreferencesInput!) {
  updatePreferences(mealPreferencesInput: $updatePreferencesInput) {
    allergens
    diets
  }
}
    `;
export const useUpdatePreferencesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdatePreferencesMutation,
    TError,
    SchemaTypes.UpdatePreferencesMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.UpdatePreferencesMutation, TError, SchemaTypes.UpdatePreferencesMutationVariables, TContext>(
    ['UpdatePreferences'],
    (variables?: SchemaTypes.UpdatePreferencesMutationVariables) =>
      graphqlFetcher<SchemaTypes.UpdatePreferencesMutation, SchemaTypes.UpdatePreferencesMutationVariables>(
        UpdatePreferencesDocument,
        variables
      )(),
    options
  );
export const OrdersDocument = `
    query Orders {
  me {
    subscription {
      ...SubscriptionFields
    }
  }
}
    ${SubscriptionFieldsFragmentDoc}`;
export const useOrdersQuery = <TData = SchemaTypes.OrdersQuery, TError = unknown>(
  variables?: SchemaTypes.OrdersQueryVariables,
  options?: UseQueryOptions<SchemaTypes.OrdersQuery, TError, TData>
) =>
  useQuery<SchemaTypes.OrdersQuery, TError, TData>(
    variables === undefined ? ['Orders'] : ['Orders', variables],
    graphqlFetcher<SchemaTypes.OrdersQuery, SchemaTypes.OrdersQueryVariables>(OrdersDocument, variables),
    options
  );
export const MyOrdersDocument = `
    query MyOrders {
  customerOrders {
    id
    status
    display_id
    first
    customer {
      id
    }
    payments {
      data
    }
    cart {
      id
      email
      billing_address_id
      shipping_address_id
      completed_at
      created_at
      items {
        id
        description
        title
        quantity
        allow_discounts
        has_shipping
        unit_price
        variant_id
        is_giftcard
        thumbnail
        variant {
          ...ProductVariantFields
        }
        product_rating {
          id
          user_id
          medusa_line_item_id
          medusa_order_id
          product_variant_id
          rating
          note
        }
      }
    }
    shipping_total
    discount_total
    tax_total
    total
    subtotal
    paid_total
  }
}
    ${ProductVariantFieldsFragmentDoc}`;
export const useMyOrdersQuery = <TData = SchemaTypes.MyOrdersQuery, TError = unknown>(
  variables?: SchemaTypes.MyOrdersQueryVariables,
  options?: UseQueryOptions<SchemaTypes.MyOrdersQuery, TError, TData>
) =>
  useQuery<SchemaTypes.MyOrdersQuery, TError, TData>(
    variables === undefined ? ['MyOrders'] : ['MyOrders', variables],
    graphqlFetcher<SchemaTypes.MyOrdersQuery, SchemaTypes.MyOrdersQueryVariables>(MyOrdersDocument, variables),
    options
  );
export const SaveProductRatingDocument = `
    mutation SaveProductRating($saveProductRatingInput: SaveProductRatingInput!) {
  saveProductRating(input: $saveProductRatingInput) {
    id
    user_id
    medusa_order_id
    medusa_line_item_id
    product_variant_id
    rating
    note
    created_at
  }
}
    `;
export const useSaveProductRatingMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.SaveProductRatingMutation,
    TError,
    SchemaTypes.SaveProductRatingMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.SaveProductRatingMutation, TError, SchemaTypes.SaveProductRatingMutationVariables, TContext>(
    ['SaveProductRating'],
    (variables?: SchemaTypes.SaveProductRatingMutationVariables) =>
      graphqlFetcher<SchemaTypes.SaveProductRatingMutation, SchemaTypes.SaveProductRatingMutationVariables>(
        SaveProductRatingDocument,
        variables
      )(),
    options
  );
export const UpdateOrderDocument = `
    mutation UpdateOrder($updateSubscriptionOrderInput: UpdateSubscriptionOrderInput!) {
  updateSubscriptionOrder(
    updateSubscriptionOrderInput: $updateSubscriptionOrderInput
  ) {
    id
    complete_order_at
    created_at
    medusa_cart_id
    processed_at
    skip
    status
    subscription_id
    updated_at
    week
    year
  }
}
    `;
export const useUpdateOrderMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdateOrderMutation,
    TError,
    SchemaTypes.UpdateOrderMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.UpdateOrderMutation, TError, SchemaTypes.UpdateOrderMutationVariables, TContext>(
    ['UpdateOrder'],
    (variables?: SchemaTypes.UpdateOrderMutationVariables) =>
      graphqlFetcher<SchemaTypes.UpdateOrderMutation, SchemaTypes.UpdateOrderMutationVariables>(
        UpdateOrderDocument,
        variables
      )(),
    options
  );
export const CheckPostalCodeDocument = `
    mutation CheckPostalCode($code: String!) {
  postalCodeAllowed(postal_code: $code)
}
    `;
export const useCheckPostalCodeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.CheckPostalCodeMutation,
    TError,
    SchemaTypes.CheckPostalCodeMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.CheckPostalCodeMutation, TError, SchemaTypes.CheckPostalCodeMutationVariables, TContext>(
    ['CheckPostalCode'],
    (variables?: SchemaTypes.CheckPostalCodeMutationVariables) =>
      graphqlFetcher<SchemaTypes.CheckPostalCodeMutation, SchemaTypes.CheckPostalCodeMutationVariables>(
        CheckPostalCodeDocument,
        variables
      )(),
    options
  );
export const ProductVariantDocument = `
    query ProductVariant($id: String!) {
  productVariant(id: $id) {
    ...ProductVariantFields
  }
}
    ${ProductVariantFieldsFragmentDoc}`;
export const useProductVariantQuery = <TData = SchemaTypes.ProductVariantQuery, TError = unknown>(
  variables: SchemaTypes.ProductVariantQueryVariables,
  options?: UseQueryOptions<SchemaTypes.ProductVariantQuery, TError, TData>
) =>
  useQuery<SchemaTypes.ProductVariantQuery, TError, TData>(
    ['ProductVariant', variables],
    graphqlFetcher<SchemaTypes.ProductVariantQuery, SchemaTypes.ProductVariantQueryVariables>(
      ProductVariantDocument,
      variables
    ),
    options
  );
export const ProductsDocument = `
    query Products($collection_title: String, $sku_filter: String) {
  productVariants(collection_title: $collection_title, sku_filter: $sku_filter) {
    ...ProductVariantFields
  }
}
    ${ProductVariantFieldsFragmentDoc}`;
export const useProductsQuery = <TData = SchemaTypes.ProductsQuery, TError = unknown>(
  variables?: SchemaTypes.ProductsQueryVariables,
  options?: UseQueryOptions<SchemaTypes.ProductsQuery, TError, TData>
) =>
  useQuery<SchemaTypes.ProductsQuery, TError, TData>(
    variables === undefined ? ['Products'] : ['Products', variables],
    graphqlFetcher<SchemaTypes.ProductsQuery, SchemaTypes.ProductsQueryVariables>(ProductsDocument, variables),
    options
  );
export const SubscriptionProductsDocument = `
    query SubscriptionProducts {
  standardSubscriptions: productVariants(
    collection_title: "Subscriptions"
    sku_filter: "standard"
  ) {
    ...ProductVariantFields
  }
}
    ${ProductVariantFieldsFragmentDoc}`;
export const useSubscriptionProductsQuery = <TData = SchemaTypes.SubscriptionProductsQuery, TError = unknown>(
  variables?: SchemaTypes.SubscriptionProductsQueryVariables,
  options?: UseQueryOptions<SchemaTypes.SubscriptionProductsQuery, TError, TData>
) =>
  useQuery<SchemaTypes.SubscriptionProductsQuery, TError, TData>(
    variables === undefined ? ['SubscriptionProducts'] : ['SubscriptionProducts', variables],
    graphqlFetcher<SchemaTypes.SubscriptionProductsQuery, SchemaTypes.SubscriptionProductsQueryVariables>(
      SubscriptionProductsDocument,
      variables
    ),
    options
  );
export const OrderedMealProductsDocument = `
    query OrderedMealProducts($currentSubscriptionOrderId: String, $filterPremiums: Boolean) {
  standardMeals: orderedMeals(
    currentSubscriptionOrderId: $currentSubscriptionOrderId
    filterPremiums: $filterPremiums
  ) {
    ...ProductVariantFields
  }
}
    ${ProductVariantFieldsFragmentDoc}`;
export const useOrderedMealProductsQuery = <TData = SchemaTypes.OrderedMealProductsQuery, TError = unknown>(
  variables?: SchemaTypes.OrderedMealProductsQueryVariables,
  options?: UseQueryOptions<SchemaTypes.OrderedMealProductsQuery, TError, TData>
) =>
  useQuery<SchemaTypes.OrderedMealProductsQuery, TError, TData>(
    variables === undefined ? ['OrderedMealProducts'] : ['OrderedMealProducts', variables],
    graphqlFetcher<SchemaTypes.OrderedMealProductsQuery, SchemaTypes.OrderedMealProductsQueryVariables>(
      OrderedMealProductsDocument,
      variables
    ),
    options
  );
export const MealProductsDocument = `
    query MealProducts {
  standardMeals: productVariants(
    collection_title: "Meals"
    sku_filter: "standard"
  ) {
    ...ProductVariantFields
  }
}
    ${ProductVariantFieldsFragmentDoc}`;
export const useMealProductsQuery = <TData = SchemaTypes.MealProductsQuery, TError = unknown>(
  variables?: SchemaTypes.MealProductsQueryVariables,
  options?: UseQueryOptions<SchemaTypes.MealProductsQuery, TError, TData>
) =>
  useQuery<SchemaTypes.MealProductsQuery, TError, TData>(
    variables === undefined ? ['MealProducts'] : ['MealProducts', variables],
    graphqlFetcher<SchemaTypes.MealProductsQuery, SchemaTypes.MealProductsQueryVariables>(
      MealProductsDocument,
      variables
    ),
    options
  );
export const ExtraProductsDocument = `
    query ExtraProducts {
  standardExtras: productVariants(
    collection_title: "Extras"
    sku_filter: "standard"
  ) {
    ...ProductVariantFields
  }
}
    ${ProductVariantFieldsFragmentDoc}`;
export const useExtraProductsQuery = <TData = SchemaTypes.ExtraProductsQuery, TError = unknown>(
  variables?: SchemaTypes.ExtraProductsQueryVariables,
  options?: UseQueryOptions<SchemaTypes.ExtraProductsQuery, TError, TData>
) =>
  useQuery<SchemaTypes.ExtraProductsQuery, TError, TData>(
    variables === undefined ? ['ExtraProducts'] : ['ExtraProducts', variables],
    graphqlFetcher<SchemaTypes.ExtraProductsQuery, SchemaTypes.ExtraProductsQueryVariables>(
      ExtraProductsDocument,
      variables
    ),
    options
  );
export const OnboardingMealSelectionDocument = `
    query OnboardingMealSelection {
  me {
    subscription {
      product_variant_id
      current_order {
        medusa_cart {
          items {
            id
            allow_discounts
            has_shipping
            is_giftcard
            quantity
            unit_price
            variant_id
            title
            metadata
            variant {
              ...ProductVariantFields
            }
          }
        }
      }
      subscription_items {
        id
        subscription_id
        product_variant_id
        product_variant {
          ...ProductVariantFields
        }
        quantity
        created_at
        updated_at
      }
    }
  }
}
    ${ProductVariantFieldsFragmentDoc}`;
export const useOnboardingMealSelectionQuery = <TData = SchemaTypes.OnboardingMealSelectionQuery, TError = unknown>(
  variables?: SchemaTypes.OnboardingMealSelectionQueryVariables,
  options?: UseQueryOptions<SchemaTypes.OnboardingMealSelectionQuery, TError, TData>
) =>
  useQuery<SchemaTypes.OnboardingMealSelectionQuery, TError, TData>(
    variables === undefined ? ['OnboardingMealSelection'] : ['OnboardingMealSelection', variables],
    graphqlFetcher<SchemaTypes.OnboardingMealSelectionQuery, SchemaTypes.OnboardingMealSelectionQueryVariables>(
      OnboardingMealSelectionDocument,
      variables
    ),
    options
  );
export const CreateStripeSetupIntentDocument = `
    mutation CreateStripeSetupIntent {
  createStripeSetupIntent {
    client_secret
  }
}
    `;
export const useCreateStripeSetupIntentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.CreateStripeSetupIntentMutation,
    TError,
    SchemaTypes.CreateStripeSetupIntentMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.CreateStripeSetupIntentMutation,
    TError,
    SchemaTypes.CreateStripeSetupIntentMutationVariables,
    TContext
  >(
    ['CreateStripeSetupIntent'],
    (variables?: SchemaTypes.CreateStripeSetupIntentMutationVariables) =>
      graphqlFetcher<SchemaTypes.CreateStripeSetupIntentMutation, SchemaTypes.CreateStripeSetupIntentMutationVariables>(
        CreateStripeSetupIntentDocument,
        variables
      )(),
    options
  );
export const UpsertExtraLineItemDocument = `
    mutation UpsertExtraLineItem($input: UpsertExtraLineItemInput!) {
  upsertExtraLineItem(input: $input) {
    medusa_cart {
      ...MedusaCartFields
      items {
        ...MedusaCartItemFields
        variant {
          ...ProductVariantFields
        }
      }
    }
  }
}
    ${MedusaCartFieldsFragmentDoc}
${MedusaCartItemFieldsFragmentDoc}
${ProductVariantFieldsFragmentDoc}`;
export const useUpsertExtraLineItemMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpsertExtraLineItemMutation,
    TError,
    SchemaTypes.UpsertExtraLineItemMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.UpsertExtraLineItemMutation,
    TError,
    SchemaTypes.UpsertExtraLineItemMutationVariables,
    TContext
  >(
    ['UpsertExtraLineItem'],
    (variables?: SchemaTypes.UpsertExtraLineItemMutationVariables) =>
      graphqlFetcher<SchemaTypes.UpsertExtraLineItemMutation, SchemaTypes.UpsertExtraLineItemMutationVariables>(
        UpsertExtraLineItemDocument,
        variables
      )(),
    options
  );
export const CreateSubscriptionDocument = `
    mutation CreateSubscription($createUserSubscriptionInput: CreateUserSubscriptionInput!) {
  createSubscription(input: $createUserSubscriptionInput) {
    product_variant_id
  }
}
    `;
export const useCreateSubscriptionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.CreateSubscriptionMutation,
    TError,
    SchemaTypes.CreateSubscriptionMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.CreateSubscriptionMutation,
    TError,
    SchemaTypes.CreateSubscriptionMutationVariables,
    TContext
  >(
    ['CreateSubscription'],
    (variables?: SchemaTypes.CreateSubscriptionMutationVariables) =>
      graphqlFetcher<SchemaTypes.CreateSubscriptionMutation, SchemaTypes.CreateSubscriptionMutationVariables>(
        CreateSubscriptionDocument,
        variables
      )(),
    options
  );
export const UpdateSubscriptionDocument = `
    mutation UpdateSubscription($updateUserSubscriptionInput: CreateUserSubscriptionInput!) {
  updateSubscription(input: $updateUserSubscriptionInput) {
    ...SubscriptionFields
  }
}
    ${SubscriptionFieldsFragmentDoc}`;
export const useUpdateSubscriptionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdateSubscriptionMutation,
    TError,
    SchemaTypes.UpdateSubscriptionMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.UpdateSubscriptionMutation,
    TError,
    SchemaTypes.UpdateSubscriptionMutationVariables,
    TContext
  >(
    ['UpdateSubscription'],
    (variables?: SchemaTypes.UpdateSubscriptionMutationVariables) =>
      graphqlFetcher<SchemaTypes.UpdateSubscriptionMutation, SchemaTypes.UpdateSubscriptionMutationVariables>(
        UpdateSubscriptionDocument,
        variables
      )(),
    options
  );
export const StartSubscriptionDocument = `
    mutation StartSubscription {
  startSubscription {
    id
    start_date
  }
}
    `;
export const useStartSubscriptionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.StartSubscriptionMutation,
    TError,
    SchemaTypes.StartSubscriptionMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.StartSubscriptionMutation, TError, SchemaTypes.StartSubscriptionMutationVariables, TContext>(
    ['StartSubscription'],
    (variables?: SchemaTypes.StartSubscriptionMutationVariables) =>
      graphqlFetcher<SchemaTypes.StartSubscriptionMutation, SchemaTypes.StartSubscriptionMutationVariables>(
        StartSubscriptionDocument,
        variables
      )(),
    options
  );
export const CancelSubscriptionDocument = `
    mutation CancelSubscription {
  cancelSubscription {
    id
    end_date
    user_id
  }
}
    `;
export const useCancelSubscriptionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.CancelSubscriptionMutation,
    TError,
    SchemaTypes.CancelSubscriptionMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.CancelSubscriptionMutation,
    TError,
    SchemaTypes.CancelSubscriptionMutationVariables,
    TContext
  >(
    ['CancelSubscription'],
    (variables?: SchemaTypes.CancelSubscriptionMutationVariables) =>
      graphqlFetcher<SchemaTypes.CancelSubscriptionMutation, SchemaTypes.CancelSubscriptionMutationVariables>(
        CancelSubscriptionDocument,
        variables
      )(),
    options
  );
export const RestartSubscriptionDocument = `
    mutation RestartSubscription {
  restartSubscription {
    ...SubscriptionFields
  }
}
    ${SubscriptionFieldsFragmentDoc}`;
export const useRestartSubscriptionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.RestartSubscriptionMutation,
    TError,
    SchemaTypes.RestartSubscriptionMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.RestartSubscriptionMutation,
    TError,
    SchemaTypes.RestartSubscriptionMutationVariables,
    TContext
  >(
    ['RestartSubscription'],
    (variables?: SchemaTypes.RestartSubscriptionMutationVariables) =>
      graphqlFetcher<SchemaTypes.RestartSubscriptionMutation, SchemaTypes.RestartSubscriptionMutationVariables>(
        RestartSubscriptionDocument,
        variables
      )(),
    options
  );
export const UpdateCurrentOrderLineItemDocument = `
    mutation UpdateCurrentOrderLineItem($input: UpdateLineItemInput!) {
  updateCurrentOrderLineItem(input: $input) {
    medusa_cart {
      ...MedusaCartFields
      items {
        ...MedusaCartItemFields
        variant {
          ...ProductVariantFields
        }
      }
    }
  }
}
    ${MedusaCartFieldsFragmentDoc}
${MedusaCartItemFieldsFragmentDoc}
${ProductVariantFieldsFragmentDoc}`;
export const useUpdateCurrentOrderLineItemMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdateCurrentOrderLineItemMutation,
    TError,
    SchemaTypes.UpdateCurrentOrderLineItemMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.UpdateCurrentOrderLineItemMutation,
    TError,
    SchemaTypes.UpdateCurrentOrderLineItemMutationVariables,
    TContext
  >(
    ['UpdateCurrentOrderLineItem'],
    (variables?: SchemaTypes.UpdateCurrentOrderLineItemMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.UpdateCurrentOrderLineItemMutation,
        SchemaTypes.UpdateCurrentOrderLineItemMutationVariables
      >(UpdateCurrentOrderLineItemDocument, variables)(),
    options
  );
export const CreateCancellationFeedbackDocument = `
    mutation CreateCancellationFeedback($input: CancellationFeedbackInput!) {
  createCancellationFeedback(input: $input) {
    id
  }
}
    `;
export const useCreateCancellationFeedbackMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.CreateCancellationFeedbackMutation,
    TError,
    SchemaTypes.CreateCancellationFeedbackMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.CreateCancellationFeedbackMutation,
    TError,
    SchemaTypes.CreateCancellationFeedbackMutationVariables,
    TContext
  >(
    ['CreateCancellationFeedback'],
    (variables?: SchemaTypes.CreateCancellationFeedbackMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.CreateCancellationFeedbackMutation,
        SchemaTypes.CreateCancellationFeedbackMutationVariables
      >(CreateCancellationFeedbackDocument, variables)(),
    options
  );
export const UpdateSubscriptionFrequencyDocument = `
    mutation UpdateSubscriptionFrequency($input: UpdateSubscriptionFrequencyInput!) {
  updateSubscriptionFrequency(input: $input) {
    id
    frequency
    upcoming_orders {
      skip
    }
  }
}
    `;
export const useUpdateSubscriptionFrequencyMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdateSubscriptionFrequencyMutation,
    TError,
    SchemaTypes.UpdateSubscriptionFrequencyMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.UpdateSubscriptionFrequencyMutation,
    TError,
    SchemaTypes.UpdateSubscriptionFrequencyMutationVariables,
    TContext
  >(
    ['UpdateSubscriptionFrequency'],
    (variables?: SchemaTypes.UpdateSubscriptionFrequencyMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.UpdateSubscriptionFrequencyMutation,
        SchemaTypes.UpdateSubscriptionFrequencyMutationVariables
      >(UpdateSubscriptionFrequencyDocument, variables)(),
    options
  );
export const RerunSubscriptionTransactionsDocument = `
    mutation RerunSubscriptionTransactions {
  rerunSubscriptionTransactions {
    ...SubscriptionFields
  }
}
    ${SubscriptionFieldsFragmentDoc}`;
export const useRerunSubscriptionTransactionsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.RerunSubscriptionTransactionsMutation,
    TError,
    SchemaTypes.RerunSubscriptionTransactionsMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.RerunSubscriptionTransactionsMutation,
    TError,
    SchemaTypes.RerunSubscriptionTransactionsMutationVariables,
    TContext
  >(
    ['RerunSubscriptionTransactions'],
    (variables?: SchemaTypes.RerunSubscriptionTransactionsMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.RerunSubscriptionTransactionsMutation,
        SchemaTypes.RerunSubscriptionTransactionsMutationVariables
      >(RerunSubscriptionTransactionsDocument, variables)(),
    options
  );
export const UnpauseSubscriptionDocument = `
    mutation UnpauseSubscription {
  unpauseSubscription {
    ...SubscriptionFields
  }
}
    ${SubscriptionFieldsFragmentDoc}`;
export const useUnpauseSubscriptionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UnpauseSubscriptionMutation,
    TError,
    SchemaTypes.UnpauseSubscriptionMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.UnpauseSubscriptionMutation,
    TError,
    SchemaTypes.UnpauseSubscriptionMutationVariables,
    TContext
  >(
    ['UnpauseSubscription'],
    (variables?: SchemaTypes.UnpauseSubscriptionMutationVariables) =>
      graphqlFetcher<SchemaTypes.UnpauseSubscriptionMutation, SchemaTypes.UnpauseSubscriptionMutationVariables>(
        UnpauseSubscriptionDocument,
        variables
      )(),
    options
  );
export const AddReturningDiscountCodeToCartDocument = `
    mutation AddReturningDiscountCodeToCart {
  addReturningDiscountCodeToCart {
    ...MedusaCartFields
    discounts {
      ...MedusaDiscountFields
    }
  }
}
    ${MedusaCartFieldsFragmentDoc}
${MedusaDiscountFieldsFragmentDoc}`;
export const useAddReturningDiscountCodeToCartMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.AddReturningDiscountCodeToCartMutation,
    TError,
    SchemaTypes.AddReturningDiscountCodeToCartMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.AddReturningDiscountCodeToCartMutation,
    TError,
    SchemaTypes.AddReturningDiscountCodeToCartMutationVariables,
    TContext
  >(
    ['AddReturningDiscountCodeToCart'],
    (variables?: SchemaTypes.AddReturningDiscountCodeToCartMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.AddReturningDiscountCodeToCartMutation,
        SchemaTypes.AddReturningDiscountCodeToCartMutationVariables
      >(AddReturningDiscountCodeToCartDocument, variables)(),
    options
  );
export const LoginDocument = `
    mutation Login($loginInput: LoginInput!) {
  login(loginInput: $loginInput) {
    token
    user {
      id
    }
  }
}
    `;
export const useLoginMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<SchemaTypes.LoginMutation, TError, SchemaTypes.LoginMutationVariables, TContext>
) =>
  useMutation<SchemaTypes.LoginMutation, TError, SchemaTypes.LoginMutationVariables, TContext>(
    ['Login'],
    (variables?: SchemaTypes.LoginMutationVariables) =>
      graphqlFetcher<SchemaTypes.LoginMutation, SchemaTypes.LoginMutationVariables>(LoginDocument, variables)(),
    options
  );
export const MeDocument = `
    query Me {
  me {
    ...UserFields
    medusa_customer {
      referral {
        id
      }
    }
  }
}
    ${UserFieldsFragmentDoc}`;
export const useMeQuery = <TData = SchemaTypes.MeQuery, TError = unknown>(
  variables?: SchemaTypes.MeQueryVariables,
  options?: UseQueryOptions<SchemaTypes.MeQuery, TError, TData>
) =>
  useQuery<SchemaTypes.MeQuery, TError, TData>(
    variables === undefined ? ['Me'] : ['Me', variables],
    graphqlFetcher<SchemaTypes.MeQuery, SchemaTypes.MeQueryVariables>(MeDocument, variables),
    options
  );
export const MyPaymentMethodsDocument = `
    query MyPaymentMethods {
  me {
    payment_methods {
      ...StripePaymentMethodFields
    }
  }
}
    ${StripePaymentMethodFieldsFragmentDoc}`;
export const useMyPaymentMethodsQuery = <TData = SchemaTypes.MyPaymentMethodsQuery, TError = unknown>(
  variables?: SchemaTypes.MyPaymentMethodsQueryVariables,
  options?: UseQueryOptions<SchemaTypes.MyPaymentMethodsQuery, TError, TData>
) =>
  useQuery<SchemaTypes.MyPaymentMethodsQuery, TError, TData>(
    variables === undefined ? ['MyPaymentMethods'] : ['MyPaymentMethods', variables],
    graphqlFetcher<SchemaTypes.MyPaymentMethodsQuery, SchemaTypes.MyPaymentMethodsQueryVariables>(
      MyPaymentMethodsDocument,
      variables
    ),
    options
  );
export const MyBoxSettingsDocument = `
    query MyBoxSettings {
  me {
    meal_preferences {
      allergens
      diets
    }
    subscription {
      product_variant {
        metadata
      }
      current_order {
        id
        medusa_cart {
          discounts {
            ...MedusaDiscountFields
          }
          items {
            variant_id
          }
        }
      }
    }
  }
}
    ${MedusaDiscountFieldsFragmentDoc}`;
export const useMyBoxSettingsQuery = <TData = SchemaTypes.MyBoxSettingsQuery, TError = unknown>(
  variables?: SchemaTypes.MyBoxSettingsQueryVariables,
  options?: UseQueryOptions<SchemaTypes.MyBoxSettingsQuery, TError, TData>
) =>
  useQuery<SchemaTypes.MyBoxSettingsQuery, TError, TData>(
    variables === undefined ? ['MyBoxSettings'] : ['MyBoxSettings', variables],
    graphqlFetcher<SchemaTypes.MyBoxSettingsQuery, SchemaTypes.MyBoxSettingsQueryVariables>(
      MyBoxSettingsDocument,
      variables
    ),
    options
  );
export const ForgotPasswordDocument = `
    mutation ForgotPassword($forgotPasswordInput: ForgotPasswordInput!) {
  forgotPassword(forgotPasswordInput: $forgotPasswordInput)
}
    `;
export const useForgotPasswordMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.ForgotPasswordMutation,
    TError,
    SchemaTypes.ForgotPasswordMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.ForgotPasswordMutation, TError, SchemaTypes.ForgotPasswordMutationVariables, TContext>(
    ['ForgotPassword'],
    (variables?: SchemaTypes.ForgotPasswordMutationVariables) =>
      graphqlFetcher<SchemaTypes.ForgotPasswordMutation, SchemaTypes.ForgotPasswordMutationVariables>(
        ForgotPasswordDocument,
        variables
      )(),
    options
  );
export const ResetPasswordDocument = `
    mutation ResetPassword($resetPasswordInput: ResetPasswordInput!) {
  resetPassword(resetPasswordInput: $resetPasswordInput) {
    token
  }
}
    `;
export const useResetPasswordMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.ResetPasswordMutation,
    TError,
    SchemaTypes.ResetPasswordMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.ResetPasswordMutation, TError, SchemaTypes.ResetPasswordMutationVariables, TContext>(
    ['ResetPassword'],
    (variables?: SchemaTypes.ResetPasswordMutationVariables) =>
      graphqlFetcher<SchemaTypes.ResetPasswordMutation, SchemaTypes.ResetPasswordMutationVariables>(
        ResetPasswordDocument,
        variables
      )(),
    options
  );
export const UpdateUserProfileDocument = `
    mutation UpdateUserProfile($userInput: UpdateUserInput!, $updateAddressInput: UpdateAddressInput!, $setPasswordInput: SetPasswordInput!) {
  updateUser(userInput: $userInput) {
    id
  }
  updateUserShippingAddress(updateAddressInput: $updateAddressInput) {
    id
  }
  setPassword(setPasswordInput: $setPasswordInput) {
    id
  }
}
    `;
export const useUpdateUserProfileMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdateUserProfileMutation,
    TError,
    SchemaTypes.UpdateUserProfileMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.UpdateUserProfileMutation, TError, SchemaTypes.UpdateUserProfileMutationVariables, TContext>(
    ['UpdateUserProfile'],
    (variables?: SchemaTypes.UpdateUserProfileMutationVariables) =>
      graphqlFetcher<SchemaTypes.UpdateUserProfileMutation, SchemaTypes.UpdateUserProfileMutationVariables>(
        UpdateUserProfileDocument,
        variables
      )(),
    options
  );
export const UpdateUserDocument = `
    mutation UpdateUser($userInput: UpdateUserInput!) {
  updateUser(userInput: $userInput) {
    ...UserFields
  }
}
    ${UserFieldsFragmentDoc}`;
export const useUpdateUserMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdateUserMutation,
    TError,
    SchemaTypes.UpdateUserMutationVariables,
    TContext
  >
) =>
  useMutation<SchemaTypes.UpdateUserMutation, TError, SchemaTypes.UpdateUserMutationVariables, TContext>(
    ['UpdateUser'],
    (variables?: SchemaTypes.UpdateUserMutationVariables) =>
      graphqlFetcher<SchemaTypes.UpdateUserMutation, SchemaTypes.UpdateUserMutationVariables>(
        UpdateUserDocument,
        variables
      )(),
    options
  );
export const UpdateUserPaymentMethodDocument = `
    mutation UpdateUserPaymentMethod($updatePaymentMethodInput: StripePaymentMethodInput!) {
  updateUserPaymentMethod(updatePaymentMethodInput: $updatePaymentMethodInput) {
    ...StripePaymentMethodFields
  }
}
    ${StripePaymentMethodFieldsFragmentDoc}`;
export const useUpdateUserPaymentMethodMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdateUserPaymentMethodMutation,
    TError,
    SchemaTypes.UpdateUserPaymentMethodMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.UpdateUserPaymentMethodMutation,
    TError,
    SchemaTypes.UpdateUserPaymentMethodMutationVariables,
    TContext
  >(
    ['UpdateUserPaymentMethod'],
    (variables?: SchemaTypes.UpdateUserPaymentMethodMutationVariables) =>
      graphqlFetcher<SchemaTypes.UpdateUserPaymentMethodMutation, SchemaTypes.UpdateUserPaymentMethodMutationVariables>(
        UpdateUserPaymentMethodDocument,
        variables
      )(),
    options
  );
export const UpdateUserStripeCustomerDocument = `
    mutation UpdateUserStripeCustomer($updateUserStripeCustomerInput: StripeCustomerInput!) {
  updateUserStripeCustomer(
    updateUserStripeCustomerInput: $updateUserStripeCustomerInput
  ) {
    id
    invoice_settings {
      default_payment_method
    }
  }
}
    `;
export const useUpdateUserStripeCustomerMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdateUserStripeCustomerMutation,
    TError,
    SchemaTypes.UpdateUserStripeCustomerMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.UpdateUserStripeCustomerMutation,
    TError,
    SchemaTypes.UpdateUserStripeCustomerMutationVariables,
    TContext
  >(
    ['UpdateUserStripeCustomer'],
    (variables?: SchemaTypes.UpdateUserStripeCustomerMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.UpdateUserStripeCustomerMutation,
        SchemaTypes.UpdateUserStripeCustomerMutationVariables
      >(UpdateUserStripeCustomerDocument, variables)(),
    options
  );
export const DetachUserPaymentMethodDocument = `
    mutation DetachUserPaymentMethod($paymentMethodId: String!) {
  detachUserPaymentMethod(paymentMethodId: $paymentMethodId)
}
    `;
export const useDetachUserPaymentMethodMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.DetachUserPaymentMethodMutation,
    TError,
    SchemaTypes.DetachUserPaymentMethodMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.DetachUserPaymentMethodMutation,
    TError,
    SchemaTypes.DetachUserPaymentMethodMutationVariables,
    TContext
  >(
    ['DetachUserPaymentMethod'],
    (variables?: SchemaTypes.DetachUserPaymentMethodMutationVariables) =>
      graphqlFetcher<SchemaTypes.DetachUserPaymentMethodMutation, SchemaTypes.DetachUserPaymentMethodMutationVariables>(
        DetachUserPaymentMethodDocument,
        variables
      )(),
    options
  );
export const SetDefaultUserPaymentMethodDocument = `
    mutation SetDefaultUserPaymentMethod($paymentMethodId: String!) {
  setDefaultUserPaymentMethod(paymentMethodId: $paymentMethodId) {
    ...StripePaymentMethodFields
  }
}
    ${StripePaymentMethodFieldsFragmentDoc}`;
export const useSetDefaultUserPaymentMethodMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.SetDefaultUserPaymentMethodMutation,
    TError,
    SchemaTypes.SetDefaultUserPaymentMethodMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.SetDefaultUserPaymentMethodMutation,
    TError,
    SchemaTypes.SetDefaultUserPaymentMethodMutationVariables,
    TContext
  >(
    ['SetDefaultUserPaymentMethod'],
    (variables?: SchemaTypes.SetDefaultUserPaymentMethodMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.SetDefaultUserPaymentMethodMutation,
        SchemaTypes.SetDefaultUserPaymentMethodMutationVariables
      >(SetDefaultUserPaymentMethodDocument, variables)(),
    options
  );
export const MyShippingAddressesDocument = `
    query MyShippingAddresses {
  me {
    shipping_addresses {
      ...UserAddressFields
    }
  }
}
    ${UserAddressFieldsFragmentDoc}`;
export const useMyShippingAddressesQuery = <TData = SchemaTypes.MyShippingAddressesQuery, TError = unknown>(
  variables?: SchemaTypes.MyShippingAddressesQueryVariables,
  options?: UseQueryOptions<SchemaTypes.MyShippingAddressesQuery, TError, TData>
) =>
  useQuery<SchemaTypes.MyShippingAddressesQuery, TError, TData>(
    variables === undefined ? ['MyShippingAddresses'] : ['MyShippingAddresses', variables],
    graphqlFetcher<SchemaTypes.MyShippingAddressesQuery, SchemaTypes.MyShippingAddressesQueryVariables>(
      MyShippingAddressesDocument,
      variables
    ),
    options
  );
export const CreateUserShippingAddressDocument = `
    mutation CreateUserShippingAddress($createAddressInput: CreateAddressInput!) {
  createUserShippingAddress(createAddressInput: $createAddressInput) {
    ...UserAddressFields
  }
}
    ${UserAddressFieldsFragmentDoc}`;
export const useCreateUserShippingAddressMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.CreateUserShippingAddressMutation,
    TError,
    SchemaTypes.CreateUserShippingAddressMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.CreateUserShippingAddressMutation,
    TError,
    SchemaTypes.CreateUserShippingAddressMutationVariables,
    TContext
  >(
    ['CreateUserShippingAddress'],
    (variables?: SchemaTypes.CreateUserShippingAddressMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.CreateUserShippingAddressMutation,
        SchemaTypes.CreateUserShippingAddressMutationVariables
      >(CreateUserShippingAddressDocument, variables)(),
    options
  );
export const UpdateUserShippingAddressDocument = `
    mutation UpdateUserShippingAddress($updateAddressInput: UpdateAddressInput!) {
  updateUserShippingAddress(updateAddressInput: $updateAddressInput) {
    ...UserAddressFields
  }
}
    ${UserAddressFieldsFragmentDoc}`;
export const useUpdateUserShippingAddressMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.UpdateUserShippingAddressMutation,
    TError,
    SchemaTypes.UpdateUserShippingAddressMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.UpdateUserShippingAddressMutation,
    TError,
    SchemaTypes.UpdateUserShippingAddressMutationVariables,
    TContext
  >(
    ['UpdateUserShippingAddress'],
    (variables?: SchemaTypes.UpdateUserShippingAddressMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.UpdateUserShippingAddressMutation,
        SchemaTypes.UpdateUserShippingAddressMutationVariables
      >(UpdateUserShippingAddressDocument, variables)(),
    options
  );
export const DeleteUserShippingAddressDocument = `
    mutation DeleteUserShippingAddress($addressId: String!) {
  deleteUserShippingAddress(addressId: $addressId)
}
    `;
export const useDeleteUserShippingAddressMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.DeleteUserShippingAddressMutation,
    TError,
    SchemaTypes.DeleteUserShippingAddressMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.DeleteUserShippingAddressMutation,
    TError,
    SchemaTypes.DeleteUserShippingAddressMutationVariables,
    TContext
  >(
    ['DeleteUserShippingAddress'],
    (variables?: SchemaTypes.DeleteUserShippingAddressMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.DeleteUserShippingAddressMutation,
        SchemaTypes.DeleteUserShippingAddressMutationVariables
      >(DeleteUserShippingAddressDocument, variables)(),
    options
  );
export const SetDefaultUserShippingAddressDocument = `
    mutation SetDefaultUserShippingAddress($addressId: String!) {
  setDefaultUserShippingAddress(addressId: $addressId) {
    ...UserAddressFields
  }
}
    ${UserAddressFieldsFragmentDoc}`;
export const useSetDefaultUserShippingAddressMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.SetDefaultUserShippingAddressMutation,
    TError,
    SchemaTypes.SetDefaultUserShippingAddressMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.SetDefaultUserShippingAddressMutation,
    TError,
    SchemaTypes.SetDefaultUserShippingAddressMutationVariables,
    TContext
  >(
    ['SetDefaultUserShippingAddress'],
    (variables?: SchemaTypes.SetDefaultUserShippingAddressMutationVariables) =>
      graphqlFetcher<
        SchemaTypes.SetDefaultUserShippingAddressMutation,
        SchemaTypes.SetDefaultUserShippingAddressMutationVariables
      >(SetDefaultUserShippingAddressDocument, variables)(),
    options
  );
export const VariantPreferenceDocument = `
    query VariantPreference($variant_id: String!) {
  variantPreference(variant_id: $variant_id) {
    id
    user_id
    product_variant_id
    favorite
    never
  }
}
    `;
export const useVariantPreferenceQuery = <TData = SchemaTypes.VariantPreferenceQuery, TError = unknown>(
  variables: SchemaTypes.VariantPreferenceQueryVariables,
  options?: UseQueryOptions<SchemaTypes.VariantPreferenceQuery, TError, TData>
) =>
  useQuery<SchemaTypes.VariantPreferenceQuery, TError, TData>(
    ['VariantPreference', variables],
    graphqlFetcher<SchemaTypes.VariantPreferenceQuery, SchemaTypes.VariantPreferenceQueryVariables>(
      VariantPreferenceDocument,
      variables
    ),
    options
  );
export const SaveFavoritePreferenceDocument = `
    mutation SaveFavoritePreference($saveFavoritePreference: SaveFavoritePreferenceInput!) {
  saveFavoritePreference(input: $saveFavoritePreference) {
    id
    user_id
    product_variant_id
    favorite
    never
  }
}
    `;
export const useSaveFavoritePreferenceMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.SaveFavoritePreferenceMutation,
    TError,
    SchemaTypes.SaveFavoritePreferenceMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.SaveFavoritePreferenceMutation,
    TError,
    SchemaTypes.SaveFavoritePreferenceMutationVariables,
    TContext
  >(
    ['SaveFavoritePreference'],
    (variables?: SchemaTypes.SaveFavoritePreferenceMutationVariables) =>
      graphqlFetcher<SchemaTypes.SaveFavoritePreferenceMutation, SchemaTypes.SaveFavoritePreferenceMutationVariables>(
        SaveFavoritePreferenceDocument,
        variables
      )(),
    options
  );
export const SaveNeverPreferenceDocument = `
    mutation SaveNeverPreference($saveNeverPreference: SaveNeverPreferenceInput!) {
  saveNeverPreference(input: $saveNeverPreference) {
    id
    user_id
    product_variant_id
    favorite
    never
  }
}
    `;
export const useSaveNeverPreferenceMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    SchemaTypes.SaveNeverPreferenceMutation,
    TError,
    SchemaTypes.SaveNeverPreferenceMutationVariables,
    TContext
  >
) =>
  useMutation<
    SchemaTypes.SaveNeverPreferenceMutation,
    TError,
    SchemaTypes.SaveNeverPreferenceMutationVariables,
    TContext
  >(
    ['SaveNeverPreference'],
    (variables?: SchemaTypes.SaveNeverPreferenceMutationVariables) =>
      graphqlFetcher<SchemaTypes.SaveNeverPreferenceMutation, SchemaTypes.SaveNeverPreferenceMutationVariables>(
        SaveNeverPreferenceDocument,
        variables
      )(),
    options
  );
